import React from "react";
import { Table, Empty, Tooltip, Typography } from "antd";
import Badge from "./Badge";
import { VAULT_VENDOR } from "../../utils/constants";

// getVaultBalance's `ledger` array comes back in one of two unrelated shapes
// depending on the vendor backing the vault:
//   - CREDIBLE (fiat):    transaction-style entries — amount, before/after balance, type, created, ...
//   - PRIMEVAULT (crypto): per-chain token balance entries — chain, symbol, tokenDecimals, balance, ...
const isPrimevault = (vendor) => (vendor || "").toUpperCase() === VAULT_VENDOR.PRIMEVAULT;

const CopyableId = ({ value: id }) => {
  if (!id) return <span className="text-xs text-gray-900">—</span>;
  return (
    <div className="flex items-center gap-1.5 whitespace-nowrap">
      <Tooltip title={id} placement="topLeft">
        <span className="font-mono text-xs text-gray-700 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded">
          {id.length > 12 ? `${id.slice(0, 8)}…` : id}
        </span>
      </Tooltip>
      <Typography.Text
        copyable={{
          text: id,
          tooltips: ["Copy full ID", "Copied!"],
        }}
      />
    </div>
  );
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtNumber = (n, decimals = 2) =>
  Number(n ?? 0).toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const CurrencyTag = ({ children }) =>
  !children ? null : (
    <span className="text-[11px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 bg-amber-50 text-amber-700 border border-amber-200">
      {children}
    </span>
  );

const TRANSACTION_COLUMNS = [
  {
    title: "SN",
    key: "sn",
    width: 60,
    fixed: "left",
    render: (_, __, idx) => <span className="text-sm text-gray-900">{idx + 1}</span>,
  },
  {
    title: "Transaction ID",
    dataIndex: "transaction_id",
    key: "transaction_id",
    width: 160,
    render: (id) => <CopyableId value={id} />,
  },
  {
    title: "Ledger ID",
    dataIndex: "ledger_id",
    key: "ledger_id",
    width: 160,
    render: (id) => <CopyableId value={id} />,
  },
  {
    title: "Type",
    key: "type",
    width: 120,
    render: (_, r) => <Badge status={Number(r.amount ?? 0) < 0 ? "DEBIT" : "CREDIT"} />,
  },
  {
    title: "Amount",
    key: "amount",
    align: "right",
    width: 180,
    render: (_, r) => {
      const amt = Number(r.amount ?? 0);
      const color = amt < 0 ? "text-red-500" : "text-green-600";
      return (
        <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${color}`}>
          {amt >= 0 ? "+" : ""}
          {fmtNumber(amt, 2)}
          <CurrencyTag>{(r.currency || "").toUpperCase()}</CurrencyTag>
        </span>
      );
    },
  },
  {
    title: "Balance After",
    key: "afterBalance",
    align: "right",
    width: 150,
    render: (_, r) => (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-900">
        {fmtNumber(r.after_balance, 2)}
        <CurrencyTag>{(r.currency || "").toUpperCase()}</CurrencyTag>
      </span>
    ),
  },
  {
    title: "Date",
    key: "date",
    width: 170,
    render: (_, r) => <span className="text-sm text-gray-900 whitespace-nowrap">{fmtDate(r.created)}</span>,
  },
];

const TOKEN_COLUMNS = [
  {
    title: "SN",
    key: "sn",
    width: 60,
    fixed: "left",
    render: (_, __, idx) => <span className="text-sm text-gray-900">{idx + 1}</span>,
  },
  {
    title: "Chain",
    key: "chain",
    render: (_, r) => <span className="text-sm text-gray-900">{r.chainName || r.chain || "—"}</span>,
  },
  {
    title: "Token",
    key: "token",
    render: (_, r) => (
      <span className="inline-flex items-center gap-1.5 text-sm text-gray-900">
        {r.name || r.symbol || "—"} <CurrencyTag>{r.symbol}</CurrencyTag>
      </span>
    ),
  },
  {
    title: "Balance",
    key: "balance",
    align: "right",
    render: (_, r) => (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900">
        {fmtNumber(r.balance, 6)} <CurrencyTag>{r.symbol}</CurrencyTag>
      </span>
    ),
  },
];

/**
 * Renders the `ledger` array embedded in a getVaultBalance() vendor entry.
 *
 * @param {object[]} ledger   — entry.ledger from the vault balance response
 * @param {string}   [vendor] — entry.balance.vendor (see VAULT_VENDOR); picks the column layout and is shown as a subtitle
 * @param {string}   [title]  — card heading, e.g. "INR Ledger"
 * @param {boolean}  [loading]
 * @param {boolean}  [disabled] — set true to hide this table entirely
 */
const VaultLedgerTable = ({ ledger, vendor, title = "Vault Ledger", loading = false, disabled = false }) => {
  if (disabled) return null;

  const entries = Array.isArray(ledger) ? ledger : [];
  const columns = isPrimevault(vendor) ? TOKEN_COLUMNS : TRANSACTION_COLUMNS;

  return (
    <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden shadow-xs mb-6">
      <div className="px-6 py-4 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800 m-0">{title}</h2>
      </div>
      <Table
        className="admin-table table-blue-zebra"
        columns={columns}
        dataSource={entries}
        rowKey={(r, idx) => r.ledger_id || r.transaction_id || `${r.chain || ""}-${r.symbol || ""}-${idx}`}
        loading={loading}
        scroll={{ x: "max-content" }}
        pagination={entries.length > 10 ? { pageSize: 10 } : false}
        locale={{
          emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No ledger entries." />,
        }}
      />
    </div>
  );
};

export default VaultLedgerTable;
